import * as React from 'react'
import {FC} from 'react'
import {observer} from 'mobx-react'
import cn from 'classnames'
import {EpmsPaymentStatus} from '@raisenow/epik'
import {useStores} from '../../../../lib/MobXProvider'
import {WidgetApi} from '../../../../lib/WidgetApi'
import {IconClose, IconOk} from '../../../ui/Icons'
import {RetryPayment} from './RetryPayment'

///////////////////////////////////////////////////////////////////////////////

export type EpmsPaymentInfoStatusProps = {
  paymentType: 'epms_payment' | 'epms_subscription'
  finalStatus?: EpmsPaymentStatus
}

///////////////////////////////////////////////////////////////////////////////

export const EpmsPaymentInfoStatus: FC<EpmsPaymentInfoStatusProps> = observer(
  (props) => {
    const {api} = useStores<{api: WidgetApi}>()
    const {paymentType, finalStatus} = props
    let paymentMethod = null

    if (!finalStatus) {
      return null
    }

    if (
      ![
        EpmsPaymentStatus.SUCCEEDED,
        EpmsPaymentStatus.PENDING,
        EpmsPaymentStatus.FAILED,
      ].includes(finalStatus)
    ) {
      return null
    }

    if (paymentType === 'epms_payment') {
      paymentMethod = api.epmsPaymentInfo?.payment_method
    } else if (paymentType === 'epms_subscription') {
      paymentMethod = api.epmsPaymentSourceInfo?.payment_method
    } else {
      return null
    }

    const baseKey = `blocks.payment_status.${paymentType}.${finalStatus}`

    let subtitle = null
    ;[`${baseKey}.subtitle_${paymentMethod}`, `${baseKey}.subtitle`].forEach(
      (key) => {
        if (!subtitle) {
          subtitle = api.trans(key, {defaultValue: null})
        }
      },
    )

    let textHTML = null
    ;[`${baseKey}.text_html_${paymentMethod}`, `${baseKey}.text_html`].forEach(
      (key) => {
        if (!textHTML) {
          textHTML = api.trans(key, {defaultValue: null})
        }
      },
    )

    const retryPaymentText = api.trans(`${baseKey}.retry_payment`)

    const showOk = [
      EpmsPaymentStatus.SUCCEEDED,
      EpmsPaymentStatus.PENDING,
    ].includes(finalStatus)

    let showRetryPaymentButton = api.resolveConfigWithSingleResult<boolean>(
      'showRetryPaymentButton',
    )
    showRetryPaymentButton = showRetryPaymentButton && !showOk

    const retryPayment = async () => {
      await api.retryPayment()
      await api.ui.scrollToTop()
    }

    return (
      <section className={cn('epms-payment-status', paymentType, finalStatus)}>
        <div className="header">
          <div className="title">
            {showOk && <IconOk />}
            <h2 className="text">{subtitle}</h2>
          </div>
          {showRetryPaymentButton && (
            <button
              type="button"
              className="close"
              onClick={retryPayment}
              aria-label={api.trans('blocks.payment_status.close_label')}
            >
              <div className="icon">
                <IconClose />
              </div>
            </button>
          )}
        </div>
        <div className="main" dangerouslySetInnerHTML={{__html: textHTML}} />

        {showRetryPaymentButton && (
          <div className="actions">
            <RetryPayment text={retryPaymentText} />
          </div>
        )}
      </section>
    )
  },
)
